/**
 * Fait : définit les schémas de validation partagés par l'API, le client et le
 *   worker — identifiants, bornes de saisie, pagination, et la validation des
 *   messages que le client a le droit d'émettre sur le canal temps réel. Un
 *   schéma valide ET type : la forme est décrite une seule fois.
 *
 * Ne fait pas : il ne décide d'aucun droit, ne parle à aucune base, ne connaît
 *   aucun fournisseur. Valider qu'une demande est bien formée n'est pas décider
 *   qu'elle est permise — ce sont deux questions, et les confondre est la
 *   manière classique de laisser passer une requête bien formée mais interdite.
 */

import { z } from 'zod';

/**
 * Identifiant de ressource.
 *
 * MongoDB rend des ObjectId de 24 caractères hexadécimaux. On refuse tout le
 * reste plutôt que de laisser une chaîne quelconque descendre jusqu'à la base.
 */
export const identifiant = z
  .string()
  .regex(/^[0-9a-f]{24}$/, 'identifiant attendu : 24 caractères hexadécimaux');

/** Adresse électronique, normalisée en minuscules. */
export const adresseElectronique = z
  .string()
  .trim()
  .toLowerCase()
  .email('adresse électronique invalide')
  .max(254, 'adresse trop longue');

/**
 * Nom donné par un humain — projet, prompt, document.
 *
 * Borné haut ET bas : une chaîne vide passerait sinon pour un nom, et créerait
 * des projets sans titre que personne ne sait plus distinguer.
 */
/**
 * La borne d'un nom donné, NOMMÉE pour que l'écran puisse la respecter.
 *
 * Elle était un chiffre nu ici, et l'écran des consignes en laissait taper
 * 200 : on écrivait un titre trop long, on l'envoyait, et l'on recevait « la
 * demande est mal formée » — sans savoir quoi corriger. Trouvé par une épreuve
 * indépendante le 2026-08-25. Une borne que le serveur connaît et que l'écran
 * ignore est une borne qui se découvre trop tard.
 */
export const LONGUEUR_MAXIMALE_NOM_DONNE = 120;

export const nomDonne = z
  .string()
  .trim()
  .min(1, 'un nom est requis')
  .max(LONGUEUR_MAXIMALE_NOM_DONNE, 'nom trop long (120 caractères au plus)');

/**
 * Une description libre — quelques phrases, jamais un roman.
 *
 * Demandée par Charles le 2026-08-04 : « le titre ne peut inspirer personne
 * alors qu'une petite description ça aide à comprendre de quoi on parle ».
 * Elle dit de quoi il retourne AVANT d'entrer — « préparation du Noël 2027 »,
 * et des détails neutres.
 *
 * La borne à 500 caractères n'est pas arbitraire : au-delà, ce n'est plus une
 * description mais une discussion, et la discussion a sa place — les
 * conversations du projet. Vide et absente sont la MÊME chose, ce qui évite
 * qu'un projet porte une description faite d'espaces.
 */
export const descriptionDonnee = z
  .string()
  .trim()
  .max(500, 'description trop longue (500 caractères au plus)');

/**
 * Rôles du produit : un parent administre la famille, les autres travaillent.
 * Ils vivent ICI, dans le contrat public — le formulaire d'invitation du
 * client en a besoin autant que le modèle de données.
 */
export const ROLES_DE_COMPTE = ['family_admin', 'member'] as const;

export type RoleDeCompte = (typeof ROLES_DE_COMPTE)[number];

/**
 * Bornes du mot de passe. Le minimum suit la recommandation d'une phrase de
 * passe ; le maximum coupe court à l'attaque du mot de passe géant, qui fait
 * travailler Argon2 pour rien.
 */
export const LONGUEUR_MINIMALE_MOT_DE_PASSE = 12;
export const LONGUEUR_MAXIMALE_MOT_DE_PASSE = 128;

/**
 * Durée de vie d'un lien de récupération de mot de passe.
 *
 * Elle vit ICI et non près du service qui la pose, parce que l'écran l'ANNONCE
 * (« le lien est valable 24 heures ») : deux endroits qui écrivent le même
 * nombre finissent par ne plus dire la même chose, et c'est l'écran qui aurait
 * menti.
 */
export const DUREE_DU_LIEN_DE_RECUPERATION_HEURES = 24;

/**
 * L'adresse à laquelle on écrit à Clannik — sa boîte à lui, pas celle de
 * quelqu'un. C'est aussi l'expéditeur de tous ses courriels : qui répond à
 * un message de Clannik écrit à cette boîte (chantier « Clannik quitte
 * Brevo », 2026-11-01 — auparavant elle n'était que l'adresse de réponse
 * d'un expéditeur sans boîte aux lettres).
 *
 * Elle vit ICI pour la même raison que la durée juste au-dessus : les
 * courriels la portent (expéditeur ET texte lisible) et la notice
 * l'ANNONCE. Trois endroits qui recopient la même adresse finissent par ne
 * plus dire la même chose, et ce serait la notice qui aurait menti.
 */
export const ADRESSE_DE_CONTACT = 'contact@clannik.chalou.link';

/** Le jeton porteur tel qu'il circule : 32 octets d'aléa en base64url. */
export const jetonPorteur = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/, 'jeton attendu : 43 caractères base64url');

/** Ce que la création d'une invitation accepte — famille_admin seulement. */
export const demandeDInvitation = z
  .object({
    adresseInvitee: adresseElectronique,
    roleAttribue: z.enum(ROLES_DE_COMPTE),
  })
  .strict();

export type DemandeDInvitation = z.infer<typeof demandeDInvitation>;

/** Ce que l'inscription accepte : le jeton reçu, un nom, un mot de passe. */
export const demandeDInscription = z
  .object({
    jeton: jetonPorteur,
    nomAffiche: nomDonne,
    motDePasse: z
      .string()
      .min(LONGUEUR_MINIMALE_MOT_DE_PASSE, 'mot de passe trop court (12 caractères au moins)')
      .max(LONGUEUR_MAXIMALE_MOT_DE_PASSE, 'mot de passe trop long (128 caractères au plus)'),
  })
  .strict();

export type DemandeDInscription = z.infer<typeof demandeDInscription>;

/**
 * Supprimer son compte exige la preuve de possession : le mot de passe, pas
 * seulement le cookie — une session volée ne suffit pas à effacer quelqu'un.
 */
export const demandeDeSuppression = z
  .object({
    motDePasse: z
      .string()
      .min(LONGUEUR_MINIMALE_MOT_DE_PASSE, 'mot de passe trop court (12 caractères au moins)')
      .max(LONGUEUR_MAXIMALE_MOT_DE_PASSE, 'mot de passe trop long (128 caractères au plus)'),
  })
  .strict();

export type DemandeDeSuppression = z.infer<typeof demandeDeSuppression>;

/**
 * Choisir un nouveau mot de passe depuis un lien reçu par courriel. Même règle
 * de longueur que l'inscription : une seule exigence dans tout le produit.
 */
export const demandeDeNouveauMotDePasse = z
  .object({
    jeton: jetonPorteur,
    motDePasse: z
      .string()
      .min(LONGUEUR_MINIMALE_MOT_DE_PASSE, 'mot de passe trop court (12 caractères au moins)')
      .max(LONGUEUR_MAXIMALE_MOT_DE_PASSE, 'mot de passe trop long (128 caractères au plus)'),
  })
  .strict();

export type DemandeDeNouveauMotDePasse = z.infer<typeof demandeDeNouveauMotDePasse>;

/** Version d'une ressource, pour l'arbitrage des écritures concurrentes. */
export const versionDeRessource = z.number().int().min(0);

/** Ce que la connexion accepte — et rien d'autre : la clé en trop échoue. */
export const demandeDeConnexion = z
  .object({
    adresseElectronique,
    motDePasse: z
      .string()
      .min(LONGUEUR_MINIMALE_MOT_DE_PASSE, 'mot de passe trop court (12 caractères au moins)')
      .max(LONGUEUR_MAXIMALE_MOT_DE_PASSE, 'mot de passe trop long (128 caractères au plus)'),
  })
  .strict();

export type DemandeDeConnexion = z.infer<typeof demandeDeConnexion>;

/** Pagination par curseur : ni page, ni décalage, qui dérivent quand on écrit. */
export const pagination = z
  .object({
    curseur: identifiant.optional(),
    limite: z.number().int().min(1).max(100).default(20),
  })
  .strict();

export type Pagination = z.infer<typeof pagination>;

/**
 * Messages que le client a le droit d'émettre sur le canal.
 *
 * Le canal reçoit du texte venant du navigateur : il est aussi hostile qu'un
 * corps de requête HTTP, et se valide donc de la même façon. Le serveur n'a
 * aucun schéma ici — il n'a pas à se valider lui-même.
 */
/**
 * Chaque variante est `strict()` : une clé inconnue fait ÉCHOUER la validation
 * au lieu d'être silencieusement retirée. Accepter puis jeter, c'est laisser un
 * client croire qu'il a été entendu — et masquer une version qui diverge.
 */
export const messageClient = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('abonner_projet'),
      emisLe: z.iso.datetime(),
      projetId: identifiant,
    })
    .strict(),
  z
    .object({
      type: z.literal('desabonner_projet'),
      emisLe: z.iso.datetime(),
      projetId: identifiant,
    })
    .strict(),
  z
    .object({
      type: z.literal('annuler_generation'),
      emisLe: z.iso.datetime(),
      generationId: identifiant,
    })
    .strict(),
  z
    .object({
      type: z.literal('battement'),
      emisLe: z.iso.datetime(),
    })
    .strict(),
]);

export type MessageClientValide = z.infer<typeof messageClient>;

/**
 * Taille maximale d'un message reçu sur le canal, en octets.
 *
 * Sans plafond, un client peut envoyer un message énorme et faire travailler le
 * serveur avant même que la validation ne commence.
 */
export const TAILLE_MAXIMALE_MESSAGE_OCTETS = 16 * 1024;
